import React, { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Stack,
  IconButton,
  Dialog,
  DialogContent,
  Box,
} from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseIcon from "@mui/icons-material/Close";
import { exportToExcel } from "../../utils/util";
import useApiEp from "../../hooks/useApiEp";

const cardSx = {
  height: "100%",
  border: "1px solid #e5eaf2",
  borderRadius: 3,
  boxShadow: "0 8px 24px rgba(20, 36, 66, 0.06)",
};

const MonthLineChart = ({ data, height, fullscreen = false }) => (
  <ResponsiveContainer width="100%" height={height}>
    <LineChart
      data={data}
      margin={{ top: 28, right: fullscreen ? 38 : 18, left: fullscreen ? 10 : -10, bottom: 10 }}
    >
      <defs>
        <linearGradient id="registrationLine" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#2f66ee" />
          <stop offset="48%" stopColor="#7c3aed" />
          <stop offset="100%" stopColor="#ef5da8" />
        </linearGradient>
      </defs>
      <CartesianGrid stroke="#e8edf5" strokeDasharray="4 5" vertical={false} />
      <XAxis
        dataKey="monthYear"
        angle={fullscreen ? 0 : -35}
        textAnchor={fullscreen ? "middle" : "end"}
        height={fullscreen ? 45 : 72}
        interval={fullscreen ? "preserveStartEnd" : 0}
        tick={{ fill: "#6d7b91", fontSize: 12, fontWeight: 500 }}
        axisLine={false}
        tickLine={false}
      />
      <YAxis
        domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]}
        tick={{ fill: "#8995a8", fontSize: 12 }}
        axisLine={false}
        tickLine={false}
        allowDecimals={false}
      />
      <Tooltip
        cursor={{ stroke: "#cbd5e1", strokeDasharray: "4 4" }}
        contentStyle={{
          border: "1px solid #e5eaf2",
          borderRadius: 12,
          boxShadow: "0 10px 28px rgba(20, 36, 66, 0.12)",
          padding: "10px 14px",
        }}
        labelStyle={{ color: "#172033", fontWeight: 700, marginBottom: 4 }}
        formatter={(value) => [Number(value).toLocaleString(), "Registrations"]}
      />
      <Legend
        iconType="circle"
        wrapperStyle={{ color: "#6d7b91", fontSize: 13, paddingTop: 8 }}
      />
      <Line
        type="monotone"
        dataKey="registrations"
        name="Registrations"
        stroke="url(#registrationLine)"
        strokeWidth={4}
        dot={{ r: 5, fill: "#ffffff", stroke: "#2f66ee", strokeWidth: 3 }}
        activeDot={{ r: 7, fill: "#7c3aed", stroke: "#ffffff", strokeWidth: 3 }}
        animationDuration={900}
      />
    </LineChart>
  </ResponsiveContainer>
);

const MonthwiseRegistrationsCard = () => {
  const { data: monthwiseData, isLoading: isMonthwiseLoading } = useApiEp(
    `/user/get-monthwise-registrations`
  );
  const [isFullscreen, setFullscreen] = useState(false);

  const dataByMonth = useMemo(() => {
    if (!monthwiseData || !monthwiseData.registrations) return [];

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    // Transform the data to include year-month label
    const transformedData = monthwiseData.registrations.map((item) => ({
      monthYear: `${monthNames[item.month - 1]} ${item.year}`,
      registrations: item.count,
      year: item.year,
      month: item.month,
      sortKey: item.year * 100 + item.month, // For proper sorting
    }));

    // Sort by year and month
    transformedData.sort((a, b) => a.sortKey - b.sortKey);

    return transformedData;
  }, [monthwiseData]);

  if (isMonthwiseLoading) {
    return (
      <Card sx={cardSx}>
        <CardContent>
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            height={250}
          >
            <CircularProgress />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={cardSx}>
        <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={0.5}>
            <Box flex={1}>
              <Typography variant="h6" fontWeight={800} color="#172033">
                Registrations by Month
              </Typography>
              <Typography variant="body2" color="#7b8799" mt={0.25}>
                Registration growth over time
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={() => exportToExcel(dataByMonth, "All_Registrations_by_Month")}
            >
              Export
            </Button>
            <IconButton onClick={() => setFullscreen(true)} aria-label="Open registrations by month fullscreen">
              <FullscreenIcon />
            </IconButton>
          </Stack>
          <MonthLineChart data={dataByMonth} height={285} />
        </CardContent>
      </Card>

      <Dialog
        open={isFullscreen}
        onClose={() => setFullscreen(false)}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogContent sx={{ p: { xs: 2, md: 3.5 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h5" fontWeight={800} color="#172033">
                Registrations by Month
              </Typography>
              <Typography variant="body2" color="#7b8799" mt={0.25}>
                Full registration history
              </Typography>
            </Box>
            <IconButton onClick={() => setFullscreen(false)} aria-label="Close fullscreen chart">
              <CloseIcon />
            </IconButton>
          </Stack>
          <MonthLineChart data={dataByMonth} height={520} fullscreen />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MonthwiseRegistrationsCard;
